import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, ListTree, Search } from 'lucide-react'
import { toast } from 'sonner'
import {
  useQualityTierCategories,
  useQualityTierCategory,
  useSetRating,
  type QualityTierCategory,
  type QualityTierCategoryDetail,
} from '@/api/quality-tiers'
import {
  MANUFACTURER_RATING_KINDS,
  MANUFACTURER_RATINGS,
  type ManufacturerRatingKind,
} from '@/lib/manufacturer-ratings'
import { RatingCommentsButton } from '@/features/quality-tiers/rating-comments'
import { RatingSelect } from '@/features/quality-tiers/rating-select'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { SearchInput } from '@/components/search-input'

const pathKey = (path: string[]) => path.join('\u0000')
const isAncestor = (parent: string[], child: string[]) =>
  parent.length < child.length && parent.every((part, index) => child[index] === part)

/**
 * Every manufacturer's ratings in every store category, on one page: Good / Better / Best
 * (USA-1258), Preference and Pricing (USA-1272). Pick a category at any level; a rating set there
 * carries to its sub-categories.
 */
export function QualityTiersPage({
  categoryId,
  onCategoryChange,
}: {
  categoryId: number | null
  onCategoryChange: (categoryId: number) => void
}) {
  const { data: categories, isLoading } = useQualityTierCategories()
  const [treeOpen, setTreeOpen] = useState(false)
  const selectedId = categoryId ?? categories?.[0]?.taxonomy_node_id ?? null
  const selected = categories?.find((category) => category.taxonomy_node_id === selectedId)

  const choose = (id: number) => {
    onCategoryChange(id)
    setTreeOpen(false)
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Quality tiers</h1>
        <Button variant="outline" className="lg:hidden" onClick={() => setTreeOpen(true)}>
          <ListTree className="h-4 w-4" />
          Categories
        </Button>
      </header>

      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <aside className="hidden lg:block">
          <div className="sticky top-20 overflow-hidden rounded-xl border bg-card">
            {isLoading ? (
              <TreeSkeleton />
            ) : (
              <CategoryTree
                categories={categories ?? []}
                selected={selected ?? null}
                onSelect={choose}
                className="max-h-[calc(100vh-7rem)]"
              />
            )}
          </div>
        </aside>

        <Sheet open={treeOpen} onOpenChange={setTreeOpen}>
          <SheetContent side="left" className="w-[22rem] max-w-[90vw] gap-0 p-0">
            <SheetHeader className="border-b">
              <SheetTitle>Categories</SheetTitle>
            </SheetHeader>
            <CategoryTree
              categories={categories ?? []}
              selected={selected ?? null}
              onSelect={choose}
              className="min-h-0 flex-1"
            />
          </SheetContent>
        </Sheet>

        <div className="min-w-0">
          {selectedId == null ? (
            isLoading ? (
              <Skeleton className="h-64" />
            ) : (
              <p className="py-12 text-center text-sm text-muted-foreground">No store categories yet</p>
            )
          ) : (
            <CategoryDetail nodeId={selectedId} onSelect={choose} />
          )}
        </div>
      </div>
    </div>
  )
}

function TreeSkeleton() {
  return (
    <div className="space-y-2 p-3">
      {Array.from({ length: 10 }).map((_, index) => (
        <Skeleton key={index} className="h-7" />
      ))}
    </div>
  )
}

function CategoryTree({
  categories,
  selected,
  onSelect,
  className,
}: {
  categories: QualityTierCategory[]
  selected: QualityTierCategory | null
  onSelect: (id: number) => void
  className?: string
}) {
  const [filter, setFilter] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const hasChildren = useMemo(() => {
    const parents = new Set<string>()
    for (const category of categories) {
      if (category.path.length > 1) parents.add(pathKey(category.path.slice(0, -1)))
    }
    return parents
  }, [categories])

  const term = filter.trim().toLowerCase()
  const matches = useMemo(() => {
    if (!term) return null
    const hits = categories.filter((category) =>
      category.path.at(-1)?.toLowerCase().includes(term),
    )
    // Keep each match's parents so it shows in place.
    const keep = new Set<string>()
    for (const hit of hits) {
      for (let level = 1; level <= hit.path.length; level += 1) {
        keep.add(pathKey(hit.path.slice(0, level)))
      }
    }
    return keep
  }, [categories, term])

  // The selected category's parents start open and can still be collapsed.
  const isOpen = (path: string[]) => {
    if (matches != null) return true
    const key = pathKey(path)
    if (collapsed.has(key)) return false
    return expanded.has(key) || (selected != null && isAncestor(path, selected.path))
  }

  const visible = categories.filter((category) => {
    if (matches && !matches.has(pathKey(category.path))) return false
    for (let level = 1; level < category.path.length; level += 1) {
      if (!isOpen(category.path.slice(0, level))) return false
    }
    return true
  })

  const toggle = (path: string[]) => {
    const key = pathKey(path)
    const open = isOpen(path)
    setExpanded((current) => {
      const next = new Set(current)
      if (open) next.delete(key)
      else next.add(key)
      return next
    })
    setCollapsed((current) => {
      const next = new Set(current)
      if (open) next.add(key)
      else next.delete(key)
      return next
    })
  }

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="relative border-b p-2">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <SearchInput
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Find a category"
          aria-label="Find a category"
          className="pl-8"
        />
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto py-1" aria-label="Store categories">
        {visible.map((category) => {
          const key = pathKey(category.path)
          const parent = hasChildren.has(key)
          const open = parent && isOpen(category.path)
          const current = selected?.taxonomy_node_id === category.taxonomy_node_id
          return (
            <li key={category.taxonomy_node_id} className="flex items-center pr-2">
              <span
                className="shrink-0"
                style={{ width: `${(category.path.length - 1) * 0.875}rem` }}
                aria-hidden="true"
              />
              {parent ? (
                <button
                  type="button"
                  aria-label={`${open ? 'Collapse' : 'Expand'} ${category.path.at(-1)}`}
                  aria-expanded={open}
                  onClick={() => toggle(category.path)}
                  className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                >
                  {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                </button>
              ) : (
                <span className="size-7 shrink-0" aria-hidden="true" />
              )}
              <button
                type="button"
                aria-current={current ? 'true' : undefined}
                onClick={() => onSelect(category.taxonomy_node_id)}
                className={cn(
                  'flex min-h-8 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                  category.path.length === 1 && 'font-semibold',
                  current && 'bg-accent font-medium',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{category.path.at(-1)}</span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'shrink-0 text-xs tabular-nums',
                    category.ranked ? 'text-foreground' : 'text-muted-foreground',
                  )}
                  title={`${category.ranked} of ${category.manufacturers} manufacturers ranked`}
                >
                  {category.ranked}/{category.manufacturers}
                </span>
                <span className="sr-only">
                  , {category.ranked} of {category.manufacturers} ranked
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

type CategoryRow = NonNullable<QualityTierCategoryDetail['manufacturer_tiers']>[number]

/** One rating on a category row: set here, what applies, and the parent it comes from. */
function rowRating(row: CategoryRow, rating: ManufacturerRatingKind) {
  const [own, effective, from] =
    rating === 'quality_tier'
      ? [row.quality_tier, row.effective_tier, row.inherited_from]
      : rating === 'preference'
        ? [row.preference, row.effective_preference, row.preference_inherited_from]
        : [row.pricing, row.effective_pricing, row.pricing_inherited_from]
  return { own: own ?? null, effective: effective ?? null, from: from?.at(-1) ?? null }
}

const ROW_GRID = '@2xl:grid-cols-[minmax(0,1fr)_repeat(3,7.75rem)_3rem]'

function CategoryDetail({ nodeId, onSelect }: { nodeId: number; onSelect: (id: number) => void }) {
  const { data: category, isLoading } = useQualityTierCategory(nodeId)
  const { data: categories } = useQualityTierCategories()
  const setRating = useSetRating()
  const [filter, setFilter] = useState('')

  if (isLoading || !category) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-28" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  const ancestors = category.path.slice(0, -1).map((_, index) => {
    const path = category.path.slice(0, index + 1)
    return {
      name: path.at(-1) ?? '',
      id: categories?.find((item) => pathKey(item.path) === pathKey(path))?.taxonomy_node_id,
    }
  })
  const term = filter.trim().toLowerCase()
  const rows = (category.manufacturer_tiers ?? []).filter(
    (row) => !term || row.name.toLowerCase().includes(term),
  )
  const name = category.path.at(-1) ?? ''

  const save = (row: CategoryRow, rating: ManufacturerRatingKind, value: string | null) =>
    setRating.mutate(
      { manufacturerUuid: row.manufacturer_uuid, nodeId, rating, value },
      {
        onError: () =>
          toast.error(
            `Could not save the ${name} ${MANUFACTURER_RATINGS[rating].label.toLowerCase()} for ${row.name}`,
          ),
      },
    )

  return (
    <section aria-labelledby="quality-tier-category" className="@container space-y-4">
      <div>
        {ancestors.length > 0 && (
          <nav aria-label="Parent categories" className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
            {ancestors.map((ancestor, index) => (
              <span key={ancestor.name} className="inline-flex items-center gap-1">
                {index > 0 && <ChevronRight className="size-3.5" aria-hidden="true" />}
                {ancestor.id ? (
                  <button
                    type="button"
                    onClick={() => onSelect(ancestor.id as number)}
                    className="rounded hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                  >
                    {ancestor.name}
                  </button>
                ) : (
                  ancestor.name
                )}
              </span>
            ))}
          </nav>
        )}
        <h2 id="quality-tier-category" className="mt-0.5 text-xl font-semibold tracking-tight">
          {name}
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground tabular-nums">
          {category.live_products.toLocaleString()} products · {category.manufacturers} manufacturers ·{' '}
          {category.ranked} rated
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border bg-card">
        <div className="relative border-b p-2">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <SearchInput
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Find a manufacturer"
            aria-label="Find a manufacturer"
            className="pl-8 sm:max-w-sm"
          />
        </div>
        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No manufacturers match</p>
        ) : (
          <>
            <div
              aria-hidden="true"
              className={cn(
                'hidden gap-x-3 border-b bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground @2xl:grid',
                ROW_GRID,
              )}
            >
              <span>Manufacturer</span>
              {MANUFACTURER_RATING_KINDS.map((rating) => (
                <span key={rating}>{MANUFACTURER_RATINGS[rating].label}</span>
              ))}
            </div>
            <ul className="divide-y">
              {rows.map((row) => {
                const context = `${row.name} in ${name}`
                const renderRating = (rating: ManufacturerRatingKind) => {
                  const { own, effective, from } = rowRating(row, rating)
                  return (
                    <RatingSelect
                      rating={rating}
                      value={own}
                      inherited={!own && effective && from ? { value: effective, from } : null}
                      context={context}
                      pending={
                        setRating.isPending &&
                        setRating.variables?.manufacturerUuid === row.manufacturer_uuid &&
                        setRating.variables.nodeId === nodeId &&
                        setRating.variables.rating === rating
                      }
                      onChange={(value) => save(row, rating, value)}
                    />
                  )
                }
                return (
                  <li
                    key={row.manufacturer_uuid}
                    className={cn('grid items-center gap-x-3 gap-y-1.5 px-4 py-2', ROW_GRID)}
                  >
                    <div className="min-w-0">
                      <Link
                        to="/manufacturers/$manuId"
                        params={{ manuId: row.manufacturer_uuid }}
                        className="font-medium hover:underline"
                      >
                        {row.name}
                      </Link>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {row.live_products.toLocaleString()} products
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 @2xl:contents">
                      {MANUFACTURER_RATING_KINDS.map((rating) => (
                        <div key={rating} className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground @2xl:sr-only">
                            {MANUFACTURER_RATINGS[rating].label}
                          </span>
                          {renderRating(rating)}
                        </div>
                      ))}
                      <RatingCommentsButton
                        className="justify-self-end"
                        nodeId={nodeId}
                        manufacturerUuid={row.manufacturer_uuid}
                        manufacturerName={row.name}
                        categoryName={name}
                        count={row.comment_count}
                        renderRating={renderRating}
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>
    </section>
  )
}
